import { COLLABORATOR_SENTINEL, EVERYONE_SENTINEL } from "@sp-crm/core";
import { Select } from "components/ui/select";
import { useGetUsersForRegionQuery } from "generated/graphql";
import React from "react";
import { useCurrentUserId, useRegionId } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";
import { SelectableUser } from "./user-select";

interface UserSelectRestricted {
    label: string | JSX.Element;
    onChange: (userId: SelectableUser) => void;
    value: SelectableUser;
    includeEveryone: boolean;
    everyoneLabel?: string;
    annotateMe?: boolean;
    disabled?: boolean;
    includeCollaboratorOption?: boolean;
}

// A restricted user select is a user select that only shows the current user, and optionally, Everyone filter.
export const UserSelectRestricted: React.FC<UserSelectRestricted> = props => {
    const {
        label,
        includeEveryone,
        everyoneLabel,
        value,
        onChange,
        disabled,
        includeCollaboratorOption,
    } = props;
    const regionId = useRegionId();
    const userId = useCurrentUserId();
    const users = useGetUsersForRegionQuery(
        { regionId },
        { ...stableQueryOptions(), enabled: !!regionId },
    );
    const annotateMe = props.annotateMe ?? true;
    if (!users.isSuccess || !users.data) {
        return (
            <Select label={label} disabled>
                <option>Loading...</option>
            </Select>
        );
    }
    const availableUsers = users.data.getUsersForRegion
        .filter(u => u.id === userId)
        .sort((a, b) =>
            a.preferredName
                .toLocaleLowerCase()
                .localeCompare(b.preferredName.toLocaleLowerCase()),
        );
    const activeUsers: Record<string, string> = {};
    availableUsers.forEach(u => {
        const actualName =
            annotateMe && u.id === userId ? `${u.preferredName} (me)` : u.preferredName;
        activeUsers[u.id] = actualName;
    });
    return (
        <Select
            label={label}
            value={value}
            disabled={disabled}
            onChange={v => onChange(v.target.value as SelectableUser)}>
            {includeEveryone ? (
                <option value={EVERYONE_SENTINEL}>{everyoneLabel ?? "Everyone"}</option>
            ) : null}
            {includeCollaboratorOption ? (
                <option value={COLLABORATOR_SENTINEL}>
                    Clients I&apos;m collaborating on
                </option>
            ) : null}
            <>
                {Object.entries(activeUsers).map(([id, name]) => (
                    <option key={id} value={id}>
                        {name}
                    </option>
                ))}
            </>
        </Select>
    );
};
